import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Switch,
  FormControlLabel, Grid, Alert, CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getOrCreateOffHoursConfig, updateOffHoursConfig } from '../../services/offHours.service';

/**
 * Component for managing off-hours configuration
 */
const OffHoursConfigPanel = () => {
  const [config, setConfig] = useState({
    isEnabled: true,
    weekdayStartTime: "17:00",
    weekdayEndTime: "09:00",
    weekendStartTime: "17:00",
    weekendEndTime: "09:00"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  useEffect(() => {
    loadConfig();
  }, []);
  
  /**
   * Load off-hours configuration
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      const existingConfig = await getOrCreateOffHoursConfig();
      setConfig(existingConfig);
    } catch (error) {
      console.error('Error loading off-hours config:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Save off-hours configuration
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      
      const result = await updateOffHoursConfig({
        id: config.id,
        isEnabled: config.isEnabled,
        weekdayStartTime: config.weekdayStartTime,
        weekdayEndTime: config.weekdayEndTime,
        weekendStartTime: config.weekendStartTime,
        weekendEndTime: config.weekendEndTime,
        updatedBy: "admin" // This should be replaced with the current user's ID
      });
      
      setConfig(result);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (error) {
      console.error('Error saving off-hours config:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Format time string from Date object
   * @param {Date} date - Date object
   * @returns {string} - Time string in HH:MM format
   */
  const formatTimeString = (date) => {
    if (!date) return null;
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  /**
   * Parse time string to Date object
   * @param {string} timeString - Time string in HH:MM format
   * @returns {Date} - Date object
   */
  const parseTimeString = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box p={2}>
      {message.text && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}
      
      <FormControlLabel
        control={
          <Switch
            checked={config.isEnabled}
            onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
          />
        }
        label="Enable Off-Hours Message"
      />
      
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Weekday Hours</Typography>
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label="Start Time"
              value={parseTimeString(config.weekdayStartTime)}
              onChange={(newValue) => {
                if (newValue) {
                  const timeStr = formatTimeString(newValue);
                  setConfig({ ...config, weekdayStartTime: timeStr });
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label="End Time"
              value={parseTimeString(config.weekdayEndTime)}
              onChange={(newValue) => {
                if (newValue) {
                  const timeStr = formatTimeString(newValue);
                  setConfig({ ...config, weekdayEndTime: timeStr });
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1">Weekend Hours</Typography>
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label="Start Time"
              value={parseTimeString(config.weekendStartTime)}
              onChange={(newValue) => {
                if (newValue) {
                  const timeStr = formatTimeString(newValue);
                  setConfig({ ...config, weekendStartTime: timeStr });
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label="End Time"
              value={parseTimeString(config.weekendEndTime)}
              onChange={(newValue) => {
                if (newValue) {
                  const timeStr = formatTimeString(newValue);
                  setConfig({ ...config, weekendEndTime: timeStr });
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
      
      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default OffHoursConfigPanel;
